import { supabase, DbNewsItem } from "./supabase";

// ─── Language types ────────────────────────────────────────────────────────────
export type LangCode = "en" | "ro" | "ru";

export const LANGUAGES: { code: LangCode; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "EN" },
  { code: "ro", label: "Română", flag: "RO" },
  { code: "ru", label: "Русский", flag: "RU" },
];

export interface LangData {
  title: string;
  excerpt: string;
  body: string;
  tags: string[];
  category_label: string;
}

export const DEFAULT_LANG_DATA: LangData = {
  title: "",
  excerpt: "",
  body: "",
  tags: [],
  category_label: "",
};

export function getLang(i18nLang: string): LangCode {
  if (i18nLang.startsWith("ro")) return "ro";
  if (i18nLang.startsWith("ru")) return "ru";
  return "en";
}

// ─── Translation overlay ───────────────────────────────────────────────────────
async function applyTranslations(
  items: DbNewsItem[],
  lang: LangCode
): Promise<DbNewsItem[]> {
  if (lang === "en" || items.length === 0) return items;

  const ids = items.map((n) => n.id);
  const { data: translations } = await supabase
    .from("news_translations")
    .select("news_id, title, excerpt, body, tags, category_label")
    .in("news_id", ids)
    .eq("language", lang);

  if (!translations || translations.length === 0) return items;

  return items.map((item) => {
    const t = (translations as Array<Record<string, unknown>>).find(
      (tr) => tr.news_id === item.id
    );
    if (!t) return item;
    return {
      ...item,
      title: (t.title as string) || item.title,
      excerpt: (t.excerpt as string) || item.excerpt,
      body: (t.body as string) || item.body,
      tags: (t.tags as string[])?.length ? (t.tags as string[]) : item.tags,
      category_label: (t.category_label as string) || item.category_label,
    };
  });
}

// ─── Public fetch helpers ──────────────────────────────────────────────────────
export async function fetchNewsItems(params: {
  lang?: LangCode;
  limit?: number;
  publishedOnly?: boolean;
}): Promise<DbNewsItem[]> {
  const { lang = "en", limit, publishedOnly = true } = params;

  let query = supabase.from("news").select("*");
  if (publishedOnly) query = query.eq("status", "published");
  query = query.order("date", { ascending: false });
  if (limit) query = query.limit(limit);

  const { data } = await query;
  if (!data) return [];

  return applyTranslations(data as DbNewsItem[], lang);
}

export async function fetchNewsItem(
  slug: string,
  lang: LangCode = "en"
): Promise<DbNewsItem | null> {
  const { data } = await supabase
    .from("news")
    .select("*")
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();

  if (!data) return null;

  const [translated] = await applyTranslations([data as DbNewsItem], lang);
  return translated ?? null;
}

export async function fetchRelatedNews(
  category: string,
  excludeSlug: string,
  lang: LangCode = "en",
  limit = 3
): Promise<DbNewsItem[]> {
  const { data } = await supabase
    .from("news")
    .select("*")
    .eq("category", category)
    .eq("status", "published")
    .neq("slug", excludeSlug)
    .order("date", { ascending: false })
    .limit(limit);

  if (!data) return [];

  return applyTranslations(data as DbNewsItem[], lang);
}

// ─── Admin: save translations ─────────────────────────────────────────────────
export async function saveNewsTranslations(
  newsId: string,
  translations: Record<string, LangData>,
  isAITranslated: Record<string, boolean> = {}
): Promise<void> {
  for (const [lang, t] of Object.entries(translations)) {
    const hasContent = t.title.trim() || t.excerpt.trim() || t.body.replace(/<[^>]*>/g, "").trim();
    if (hasContent) {
      await supabase.from("news_translations").upsert(
        {
          news_id: newsId,
          language: lang,
          title: t.title,
          excerpt: t.excerpt,
          body: t.body,
          tags: t.tags,
          category_label: t.category_label,
          is_ai_translated: isAITranslated[lang] ?? false,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "news_id,language" }
      );
    } else {
      await supabase
        .from("news_translations")
        .delete()
        .eq("news_id", newsId)
        .eq("language", lang);
    }
  }
}

// ─── Admin: load translations for an article ──────────────────────────────────
export async function loadNewsTranslations(
  newsId: string
): Promise<{
  translations: Record<string, LangData>;
  isAITranslated: Record<string, boolean>;
}> {
  const { data } = await supabase
    .from("news_translations")
    .select("language, title, excerpt, body, tags, category_label, is_ai_translated")
    .eq("news_id", newsId);

  const translations: Record<string, LangData> = {};
  const isAITranslated: Record<string, boolean> = {};

  (data ?? []).forEach((t: Record<string, unknown>) => {
    translations[t.language as string] = {
      title: (t.title as string) ?? "",
      excerpt: (t.excerpt as string) ?? "",
      body: (t.body as string) ?? "",
      tags: (t.tags as string[]) ?? [],
      category_label: (t.category_label as string) ?? "",
    };
    isAITranslated[t.language as string] = (t.is_ai_translated as boolean) ?? false;
  });

  return { translations, isAITranslated };
}
